import { useState } from "react";
import type {
  OpenCollaborationInsightsRelationships,
  TargetCollaborationInsightsRelationships,
} from "@/api/collaborations";
import { Skeleton } from "@/components/ui/skeleton";
import { FindMoreCreatorsCard } from "./find-more-creators-card";

type RelationshipsData = TargetCollaborationInsightsRelationships | OpenCollaborationInsightsRelationships;

type Props = {
  collaborationId: string;
  data: RelationshipsData | null | undefined;
  isLoading: boolean;
  showRecommendations?: boolean;
  selectedKey?: string | null;
  onSelect?: (key: string | null) => void;
};

const CREATORS_TABLE_ANCHOR_ID = "collaboration-creators-list";

function AvatarStack({
  avatars,
  totalCount,
  maxVisible = 3,
}: {
  avatars: RelationshipsData["avatars"];
  totalCount: number;
  maxVisible?: number;
}) {
  if (totalCount === 0) return null;
  const visible = avatars.slice(0, maxVisible);
  const overflow = Math.max(0, totalCount - visible.length);
  return (
    <div className="flex -space-x-2 items-center">
      {visible.map((a, i) => (
        <div
          key={a.tiktokCreatorOpenId}
          className="w-7 h-7 rounded-full border-2 border-[#3D2653] bg-white/20 overflow-hidden"
          style={{ zIndex: visible.length - i }}
          title={a.nickname ?? a.tiktokCreatorOpenId}
        >
          {a.avatar ? (
            <img src={a.avatar} alt={a.nickname ?? ""} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-white/80">
              {(a.nickname ?? "?")[0]?.toUpperCase()}
            </div>
          )}
        </div>
      ))}
      {overflow > 0 && (
        <div
          className="w-7 h-7 rounded-full border-2 border-[#3D2653] bg-[#4B3360] flex items-center justify-center text-[10px] font-semibold text-white"
          style={{ zIndex: 0 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

function BucketCard({
  bucketKey,
  title,
  count,
  hoveredKey,
  selectedKey,
  setHoveredKey,
  onActivate,
}: {
  bucketKey: string;
  title: string;
  count: number;
  hoveredKey: string | null;
  selectedKey: string | null;
  setHoveredKey: (k: string | null) => void;
  onActivate: () => void;
}) {
  const isHovered = hoveredKey === bucketKey;
  const isSelected = selectedKey === bucketKey;
  const active = isHovered || isSelected;
  const hasHover = hoveredKey !== null;
  const hasSelection = selectedKey !== null;
  const dimmed = hasHover ? !isHovered && !isSelected : hasSelection ? !isSelected : false;

  return (
    <button
      type="button"
      onMouseEnter={() => setHoveredKey(bucketKey)}
      onMouseLeave={() => setHoveredKey(null)}
      onClick={onActivate}
      aria-label={`Filter creators by ${title}`}
      aria-pressed={isSelected}
      className={`flex flex-col gap-2 p-4 rounded-xl flex-1 cursor-pointer outline-none text-left transition-all duration-200 ${
        dimmed ? "opacity-35" : "opacity-100"
      }`}
      style={{
        border: `1px solid ${active ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.10)"}`,
        background: active ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.04)",
        boxShadow: isSelected ? "0 2px 8px rgba(0,0,0,0.15)" : isHovered ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
      }}
    >
      <span className="text-sm text-white/70">{title}</span>
      <span className="text-3xl font-semibold text-white leading-none">{count.toLocaleString()}</span>
      <span className="text-sm font-medium text-white text-left w-fit hover:underline mt-1">Filter Creators</span>
    </button>
  );
}

export function InsightsRelationshipsTab({
  collaborationId,
  data,
  isLoading,
  showRecommendations,
  selectedKey = null,
  onSelect,
}: Props) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const scrollToCreators = () => {
    const el = document.getElementById(CREATORS_TABLE_ANCHOR_ID);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleActivate = (bucketKey: string) => {
    const next = selectedKey === bucketKey ? null : bucketKey;
    onSelect?.(next);
    window.setTimeout(scrollToCreators, 50);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 py-2">
        <div className="flex gap-6">
          <div className="flex-1 flex flex-col gap-2">
            <Skeleton className="h-5 w-64 bg-white/20" />
            <Skeleton className="h-4 w-full max-w-md bg-white/10" />
          </div>
          {showRecommendations && <Skeleton className="h-20 w-72 bg-white/10 rounded-xl" />}
        </div>
        <div className="flex gap-3">
          {(["bucket-a", "bucket-b", "bucket-c"] as const).map((key) => (
            <Skeleton key={key} className="h-28 flex-1 rounded-xl bg-white/10" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-sm text-white/50 text-center py-6">No relationship data available</div>;
  }

  const { buckets, avatars } = data;
  const totalCreators = buckets.workedWith3Plus + buckets.workedWith1To2 + buckets.firstTime;

  return (
    <div className="flex flex-col gap-6 py-2">
      {/* Header: stacked below ~1100 px, side-by-side above */}
      <div className="flex flex-col gap-3 panels:flex-row panels:items-start panels:gap-6">
        <div className="flex-1 flex flex-col gap-2 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-base font-medium text-white">Creator Relationship Breakdown</p>
            <AvatarStack avatars={avatars} totalCount={totalCreators} />
          </div>
          <p className="text-sm text-white/60">
            Established creators typically convert better. Here's how this collaboration breaks down by how many times
            you've worked together.
          </p>
        </div>
        {showRecommendations && <FindMoreCreatorsCard collaborationId={collaborationId} layout="inline" />}
      </div>

      {/* Bucket cards */}
      <div className="flex gap-3">
        <BucketCard
          bucketKey="3plus"
          title="Worked with 3+ times"
          count={buckets.workedWith3Plus}
          hoveredKey={hoveredKey}
          selectedKey={selectedKey}
          setHoveredKey={setHoveredKey}
          onActivate={() => handleActivate("3plus")}
        />
        <BucketCard
          bucketKey="1to2"
          title="Worked with 1–2 times"
          count={buckets.workedWith1To2}
          hoveredKey={hoveredKey}
          selectedKey={selectedKey}
          setHoveredKey={setHoveredKey}
          onActivate={() => handleActivate("1to2")}
        />
        <BucketCard
          bucketKey="first"
          title="First-time invites"
          count={buckets.firstTime}
          hoveredKey={hoveredKey}
          selectedKey={selectedKey}
          setHoveredKey={setHoveredKey}
          onActivate={() => handleActivate("first")}
        />
      </div>
    </div>
  );
}
